import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, MoreHorizontal, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { AdminAttendanceDialog } from '@/pages/attendance/punch-dialog'
import { PunchInfoDialog } from '@/pages/attendance/punch-info-dialog'
import { AttendanceRequestDialog } from '@/pages/attendance/request-dialog'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { locationsApi } from '@/services/locations'
import { workersApi, type WorkerAttendanceRequestPayload } from '@/services/workers'
import {
  formatClock,
  formatDay,
  geofenceFlag,
  methodLabel,
  punchDeviceLabel,
  punchDate,
  punchStatus,
  punchWorkerId,
  todayIsoDate,
  useAttendanceDirectory,
  useAttendanceOrgTimeZone,
  useAttendancePunchActions,
  useAttendancePunches,
  workedLabel,
  workerDepartment,
  workerName,
  type AttendancePunch,
  type PunchStatus,
} from '@/pages/attendance/data'

const STATUS_LABEL: Record<PunchStatus, string> = {
  complete: 'Complete',
  working: 'Working',
  missing: 'Missing punch',
  absent: 'Not clocked in',
}

export default function TimeLogsPage() {
  const orgTimeZone = useAttendanceOrgTimeZone()
  const today = todayIsoDate(orgTimeZone)
  const [pickedStartDate, setStartDate] = useState<string | null>(null)
  const [pickedEndDate, setEndDate] = useState<string | null>(null)
  const startDate = pickedStartDate ?? today
  const endDate = pickedEndDate ?? today
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | PunchStatus>('all')
  const [locationFilter, setLocationFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [manualOpen, setManualOpen] = useState(false)
  const [requestPunch, setRequestPunch] = useState<AttendancePunch | null>(null)
  const [infoPunch, setInfoPunch] = useState<AttendancePunch | null>(null)
  const queryClient = useQueryClient()
  const { workers, byId, isLoading, error } = useAttendanceDirectory()
  const punchesQuery = useAttendancePunches(startDate, endDate)
  const locationsQuery = useQuery({
    queryKey: ['attendance-live-locations'],
    queryFn: () => locationsApi.list(),
  })
  const requestsQuery = useQuery({
    queryKey: ['attendance-requests', 'pending'],
    queryFn: () => workersApi.getAdminAttendanceRequests({ status: 'pending' }),
  })
  const { record } = useAttendancePunchActions()
  const createRequest = useMutation({
    mutationFn: (payload: WorkerAttendanceRequestPayload) => workersApi.createAdminAttendanceRequest(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attendance-requests'] })
    },
  })
  const punches = (punchesQuery.data?.results ?? []) as AttendancePunch[]
  const pendingAttendanceIds = new Set(
    (requestsQuery.data ?? [])
      .filter((request) => request.status === 'pending' && request.attendance != null)
      .map((request) => String(request.attendance)),
  )

  const mappedRows = useMemo(() => punches.map((punch) => {
    const worker = byId.get(punchWorkerId(punch)) ?? null
    return { punch, worker, status: punchStatus(punch, today), department: workerDepartment(worker) }
  }), [byId, punches, today])

  const locationOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const location of locationsQuery.data ?? []) {
      if (location.deleted || location.active === false) continue
      options.set(String(location.id), location.place || `Location ${location.id}`)
    }
    for (const row of mappedRows) {
      const id = row.punch.attendance_location_id
      const name = row.punch.attendance_location_name
      if (id != null && name && !options.has(String(id))) options.set(String(id), name)
    }
    return [...options.entries()].sort((left, right) => left[1].localeCompare(right[1]))
  }, [locationsQuery.data, mappedRows])

  const departmentOptions = useMemo(() => {
    const names = new Set<string>()
    for (const worker of workers) {
      const name = workerDepartment(worker)
      if (name && name !== '—') names.add(name)
    }
    for (const row of mappedRows) {
      if (row.department && row.department !== '—') names.add(row.department)
    }
    return [...names].sort((left, right) => left.localeCompare(right))
  }, [mappedRows, workers])

  const rows = useMemo(() => mappedRows.filter((row) => {
    const name = `${workerName(row.worker)} ${row.worker?.worker_code || ''}`.toLowerCase()
    if (search.trim() && !name.includes(search.trim().toLowerCase())) return false
    if (status !== 'all' && row.status !== status) return false
    if (locationFilter !== 'all' && String(row.punch.attendance_location_id ?? '') !== locationFilter) return false
    if (departmentFilter !== 'all' && row.department !== departmentFilter) return false
    return true
  }), [departmentFilter, locationFilter, mappedRows, search, status])

  const working = rows.filter((row) => row.status === 'working').length
  const attention = rows.filter((row) => row.status === 'missing').length
  const minutes = rows.reduce((sum, row) => sum + Number(row.punch.total_minutes || 0), 0)

  const exportCsv = () => {
    const header = ['Employee', 'Date', 'Department', 'Clock in', 'Clock out', 'Duration', 'Source', 'Status']
    const body = rows.map((row) => [
      workerName(row.worker),
      punchDate(row.punch),
      workerDepartment(row.worker),
      formatClock(row.punch.check_in_local || row.punch.check_in),
      formatClock(row.punch.check_out_local || row.punch.check_out),
      workedLabel(row.punch),
      methodLabel(row.punch.source),
      STATUS_LABEL[row.status],
    ])
    const csv = [header, ...body].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `time-logs-${startDate}-to-${endDate}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Time Logs</h1>
          <p className="mt-1 text-sm text-slate-500">Review punches, fix exceptions and keep employee hours accurate.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button type="button" onClick={() => setManualOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Manual Punch
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Stat label="Punches" value={String(rows.length)} detail="In the selected range" />
        <Stat label="Currently working" value={String(working)} detail="Open punches" />
        <Stat label="Needs attention" value={String(attention)} detail="Missing or unusual punches" />
        <Stat label="Total hours" value={`${Math.round(minutes / 60)}h`} detail="From recorded minutes" />
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap gap-2 border-b border-slate-100 p-3">
          <Input className="max-w-xs" placeholder="Search employee or ID..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <Input
            type="date"
            className="w-[160px]"
            aria-label="From date"
            value={startDate}
            max={endDate}
            onChange={(event) => {
              const next = event.target.value || today
              setStartDate(next)
              if (next > endDate) setEndDate(next)
            }}
          />
          <Input
            type="date"
            className="w-[160px]"
            aria-label="To date"
            value={endDate}
            min={startDate}
            onChange={(event) => {
              const next = event.target.value || today
              setEndDate(next)
              if (next < startDate) setStartDate(next)
            }}
          />
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Location">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locationOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Department">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departmentOptions.map((name) => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(value) => setStatus(value as 'all' | PunchStatus)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="working">Working</SelectItem>
              <SelectItem value="missing">Needs attention</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isLoading || punchesQuery.isLoading ? (
          <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
        ) : error || punchesQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(error || punchesQuery.error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No punches match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {['Employee', 'Date', 'Department', 'Clock in', 'Clock out', 'Duration', 'Source', 'Status', ''].map((heading) => (
                    <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.punch.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <WorkerProfileLink workerId={row.worker?.worker_id ?? row.worker?.id ?? punchWorkerId(row.punch)} name={workerName(row.worker)} className="font-medium text-slate-900" />
                      <div className="text-xs text-slate-500">{row.worker?.worker_code || punchWorkerId(row.punch)}</div>
                    </td>
                    <td className="px-4 py-3">{formatDay(row.punch.check_in_local || row.punch.check_in)}</td>
                    <td className="px-4 py-3 text-slate-600">{workerDepartment(row.worker)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(row.punch.check_in_local || row.punch.check_in)}</td>
                    <td className="px-4 py-3 tabular-nums">{formatClock(row.punch.check_out_local || row.punch.check_out)}</td>
                    <td className="px-4 py-3 font-medium">{workedLabel(row.punch)}</td>
                    <td className="px-4 py-3">
                      <div>{methodLabel(row.punch.source)}</div>
                      {punchDeviceLabel(row.punch) ? (
                        <div className="text-[10px] leading-tight text-slate-500">{punchDeviceLabel(row.punch)}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.status === 'complete' ? 'bg-emerald-50 text-emerald-700' : row.status === 'working' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                          {STATUS_LABEL[row.status]}
                        </span>
                        {geofenceFlag(row.punch) ? (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700" title={geofenceFlag(row.punch)?.title}>
                            {geofenceFlag(row.punch)?.label}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Time log actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setInfoPunch(row.punch)}>
                            Info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={pendingAttendanceIds.has(String(row.punch.id))}
                            onClick={() => setRequestPunch(row.punch)}
                          >
                            {pendingAttendanceIds.has(String(row.punch.id)) ? 'Request pending' : 'Raise request'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AdminAttendanceDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        workers={workers}
        isPending={record.isPending}
        onSubmit={async (payload) => {
          const result = await record.mutateAsync(payload)
          const note = (result as { punch_note?: string; punch_applied?: boolean } | undefined)?.punch_note
          if (note && (result as { punch_applied?: boolean })?.punch_applied === false) {
            throw new Error(note)
          }
          toast.success(payload.status ? 'Check-in recorded' : 'Check-out recorded')
        }}
      />
      <PunchInfoDialog
        open={Boolean(infoPunch)}
        onOpenChange={(open) => { if (!open) setInfoPunch(null) }}
        punch={infoPunch}
        employeeName={workerName(infoPunch ? byId.get(punchWorkerId(infoPunch)) : null)}
      />
      <AttendanceRequestDialog
        key={requestPunch?.id ?? 'none'}
        open={Boolean(requestPunch)}
        onOpenChange={(open) => { if (!open) setRequestPunch(null) }}
        employeeName={workerName(requestPunch ? byId.get(punchWorkerId(requestPunch)) : null)}
        workerId={requestPunch ? punchWorkerId(requestPunch) : ''}
        workDate={requestPunch ? punchDate(requestPunch) : ''}
        currentCheckIn={requestPunch ? formatClock(requestPunch.check_in_local || requestPunch.check_in) : undefined}
        currentCheckOut={requestPunch && (requestPunch.check_out || requestPunch.check_out_local) ? formatClock(requestPunch.check_out_local || requestPunch.check_out) : undefined}
        isPending={createRequest.isPending}
        onSubmit={async (payload) => {
          await createRequest.mutateAsync({
            worker_id: payload.workerId,
            attendance_id: requestPunch?.id,
            work_date: payload.workDate,
            request_type: payload.requestType,
            requested_check_in: payload.requestedCheckIn,
            requested_check_out: payload.requestedCheckOut,
            reason: payload.reason,
          })
          toast.success(payload.requestType === 'missing_punch' ? 'Missing punch request raised' : 'Time correction request raised')
        }}
      />
    </div>
  )
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{detail}</div>
    </div>
  )
}
